import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { usePdfDocument } from "../../../hooks/usePdfDocument";
import { PdfPageCanvas } from "./PdfPageCanvas";
import { ViewerToolbar } from "../shared/ViewerToolbar";
import { printDocument } from "../../../Utils/printDocument";

const ZOOM_STEP = 0.25;
const MIN_SCALE = 0.25;
const MAX_SCALE = 4;

type PdfCanvasAdapterProps = {
  url: string;
  fileName?: string;
  nav?: ReactNode;
  isToolbarActive?: boolean;
};

export const PdfCanvasAdapter = ({
  url,
  fileName,
  nav,
  isToolbarActive,
}: PdfCanvasAdapterProps) => {
  const { doc, numPages, loading, error: loadError } = usePdfDocument(url);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [error, setError] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);
    const [firstPageWidth, setFirstPageWidth] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const hasAutoFitRef = useRef(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<Record<number, HTMLDivElement>>({});
  const visibleRatios = useRef<Record<number, number>>({});

    useEffect(() => {
    setScale(1);
    setRotation(0);
    setCurrentPage(1);
    pageRefs.current = {};
    visibleRatios.current = {};
    hasAutoFitRef.current = false; 
  }, [url]);

  const zoomIn = () => setScale((s) => Math.min(MAX_SCALE, +(s + ZOOM_STEP).toFixed(2)));
  const zoomOut = () => setScale((s) => Math.max(MIN_SCALE, +(s - ZOOM_STEP).toFixed(2)));

    const fitToWidth = useCallback(() => {
    const stage = stageRef.current;
    if (!stage || !firstPageWidth || !doc) return;
    const intrinsicWidth = firstPageWidth / scale;
    const available = stage.clientWidth - 32;
    const nextScale = available / intrinsicWidth;
    setScale(+Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextScale)).toFixed(3));
  }, [firstPageWidth, scale, doc]);

  useEffect(() => {
    if (hasAutoFitRef.current || !firstPageWidth || !doc) return;
    hasAutoFitRef.current = true;
    fitToWidth();
    
  }, [firstPageWidth, doc]);

  const toggleFullscreen = async () => {
    const node = containerRef.current;
    if (!node) return;
    try {
      if (!document.fullscreenElement) {
        await node.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch {
      setError("Fullscreen isn't available in this browser.");
    }
  };

  useEffect(() => {
    const handleChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleChange);
    return () => document.removeEventListener("fullscreenchange", handleChange);
  }, []);

  const handleVisibilityChange = useCallback((pageNumber: number, ratio: number) => {
    visibleRatios.current[pageNumber] = ratio;
    let best = currentPage;
    let bestRatio = 0;
    Object.entries(visibleRatios.current).forEach(([p, r]) => {
      if (r > bestRatio) {
        bestRatio = r;
        best = Number(p);
      }
    });
    if (bestRatio > 0) setCurrentPage(best);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const registerPageRef = useCallback((pageNumber: number, el: HTMLDivElement | null) => {
    if (el) pageRefs.current[pageNumber] = el;
    else delete pageRefs.current[pageNumber];
  }, []);

  const goToPage = useCallback(
    (target: number) => {
      const clamped = Math.min(Math.max(target, 1), numPages || 1);
      pageRefs.current[clamped]?.scrollIntoView({ behavior: "smooth", block: "start" });
      setCurrentPage(clamped);
    },
    [numPages]
  );

  if (loadError) {
    return (
      <div className="doc-viewer">
        <ViewerToolbar downloadUrl={url} fileName={fileName} nav={nav} isActive={isToolbarActive} />
        <div className="doc-viewer-stage doc-viewer-stage--message">{loadError}</div>
      </div>
    );
  }

  return (
    <div className="doc-viewer" ref={containerRef}>
      <ViewerToolbar
        scale={scale}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onFitWidth={fitToWidth}
        onToggleFullscreen={toggleFullscreen}
        isFullscreen={isFullscreen}
        onRotateLeft={() => setRotation((r) => (r - 90 + 360) % 360)}
        onRotateRight={() => setRotation((r) => (r + 90) % 360)}
        currentPage={currentPage}
        totalPages={numPages}
        onPrevPage={() => goToPage(currentPage - 1)}
        onNextPage={() => goToPage(currentPage + 1)}
        onPrint={() => printDocument(url, "pdf")}
        downloadUrl={url}
        fileName={fileName}
        nav={nav}
        isActive={isToolbarActive}
        onDownloadError={setError}
      />
      <div className="doc-viewer-stage doc-viewer-stage--scroll" ref={stageRef}>
        {loading || !doc ? (
          <div className="doc-viewer-stage--message">Loading document…</div>
        ) : (
          <div className="pdf-pages">
            {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNumber) => (
              <PdfPageCanvas
                key={pageNumber}
                doc={doc}
                pageNumber={pageNumber}
                scale={scale}
                rotation={rotation}
                onFirstPageSize={pageNumber === 1 ? (w) => setFirstPageWidth(w) : undefined}
                onVisibilityChange={handleVisibilityChange}
                registerPageRef={registerPageRef}
              />
            ))}
          </div>
        )}
      </div>
      {error && <div className="doc-viewer-error">{error}</div>}
    </div>
  );
};